import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Textarea — multi-line text entry. Mirrors the Input primitive's tokens and
 * error handling so forms stay visually consistent.
 * Visual state (default/error) is driven by the `error` prop or `aria-invalid`.
 */

export interface TextareaProps
    extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    error?: boolean
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ className, error, 'aria-invalid': ariaInvalid, ...props }, ref) => {
        const isInvalid = Boolean(
            error || ariaInvalid === true || ariaInvalid === 'true',
        )
        return (
            <textarea
                aria-invalid={isInvalid || undefined}
                className={cn(
                    'flex min-h-20 w-full rounded-md border bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] transition-colors duration-200',
                    'placeholder:text-[var(--text-tertiary)]',
                    'focus:outline-none focus:ring-2',
                    'disabled:cursor-not-allowed disabled:bg-[var(--bg-subtle)] disabled:border-[var(--border-subtle)] disabled:text-[var(--text-disabled)] disabled:placeholder:text-[var(--text-disabled)]',
                    isInvalid
                        ? 'border-[var(--border-danger)] focus:border-[var(--border-danger)] focus:ring-[var(--danger-100)]'
                        : 'border-[var(--border-input)] focus:border-[var(--border-focus)] focus:ring-[var(--primary-200)]',
                    className,
                )}
                ref={ref}
                {...props}
            />
        )
    },
)
Textarea.displayName = 'Textarea'

export { Textarea }
